import { Box, Fab, Stack, useTheme } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";
import GeoMapJson from "features/analytics/assets/mapFeatures.json";

interface MapPosition {
  coordinates: [number, number];
  zoom: number;
}

const DEFAULT_TEST_REGION: [number, number] = [-2.2426, 53.4808];
const CONTROL_REGIONS: [number, number][] = [
  [-1.5491, 53.8008], // Leeds
  [-2.9916, 53.4084], // Liverpool
  [-1.8904, 52.4862], // Birmingham
  [-1.1581, 52.9548], // Nottingham
];

interface IncrementalityTestRegionMapProps {
  testRegionCoordinates?: [number, number];
}

function IncrementalityTestRegionMap({
  testRegionCoordinates,
}: IncrementalityTestRegionMapProps) {
  const theme = useTheme();
  const [position, setPosition] = useState<MapPosition>({
    coordinates: [-2, 54.8],
    zoom: 4.1,
  });

  const handleZoomIn = useCallback(() => {
    setPosition((prev) => ({ ...prev, zoom: Math.min(prev.zoom * 1.2, 10) }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setPosition((prev) => ({ ...prev, zoom: Math.max(prev.zoom / 1.2, 2.8) }));
  }, []);

  const testRegion = useMemo<[number, number]>(() => {
    return testRegionCoordinates || DEFAULT_TEST_REGION;
  }, [testRegionCoordinates]);

  useEffect(() => {
    if (!testRegionCoordinates) return;
    setPosition((prev) => ({
      ...prev,
      coordinates: testRegionCoordinates,
    }));
  }, [testRegionCoordinates]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        overflow: "hidden",
      }}
    >
      <Stack
        spacing={1}
        sx={{ position: "absolute", right: 8, top: 8, zIndex: 2 }}
      >
        <Fab size="small" color="secondary" onClick={handleZoomIn}>
          <Add fontSize="small" />
        </Fab>
        <Fab size="small" color="secondary" onClick={handleZoomOut}>
          <Remove fontSize="small" />
        </Fab>
      </Stack>

      <ComposableMap
        projection="geoMercator"
        style={{ width: "100%", height: "100%" }}
      >
        <ZoomableGroup
          center={position.coordinates}
          zoom={position.zoom}
          onMoveEnd={(next) => setPosition(next as MapPosition)}
        >
          <Geographies geography={GeoMapJson}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={theme.palette.primary.light + "18"}
                  stroke={theme.palette.grey[400]}
                  strokeWidth={0.2}
                  style={{
                    default: { outline: "none" },
                    hover: { outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>

          {CONTROL_REGIONS.map((coords, idx) => (
            <Marker key={`control-${idx}`} coordinates={coords}>
              <circle
                r={4}
                fill={theme.palette.success.main}
                stroke="#fff"
                strokeWidth={1.2}
              />
            </Marker>
          ))}

          <Marker coordinates={testRegion}>
            <circle
              r={5}
              fill={theme.palette.primary.main}
              stroke="#fff"
              strokeWidth={1.3}
            />
          </Marker>
        </ZoomableGroup>
      </ComposableMap>
    </Box>
  );
}

export default IncrementalityTestRegionMap;
